import { apiClient } from './client';

export async function getSettings(): Promise<Record<string, string>> {
  const { data } = await apiClient.get('/settings');
  return data.data;
}

export async function updateSettings(updates: Record<string, string>): Promise<void> {
  await apiClient.put('/settings', updates);
}

// ── API Key Management ──────────────────────────────────
export async function getApiKeyStatus(): Promise<{ has_key: boolean; masked: string | null }> {
  const { data } = await apiClient.get('/settings/api-key');
  return data;
}

export async function saveApiKey(api_key: string): Promise<{ ok: boolean; masked: string }> {
  const { data } = await apiClient.put('/settings/api-key', { api_key });
  return data;
}

export async function deleteApiKey(): Promise<void> {
  await apiClient.delete('/settings/api-key');
}

// ── Brave Search API Key ────────────────────────────────
export async function getBraveKeyStatus(): Promise<{ has_key: boolean; masked: string | null }> {
  const { data } = await apiClient.get('/settings/brave-api-key');
  return data;
}

export async function saveBraveKey(api_key: string): Promise<{ ok: boolean; masked: string }> {
  const { data } = await apiClient.put('/settings/brave-api-key', { api_key });
  return data;
}

export async function deleteBraveKey(): Promise<void> {
  await apiClient.delete('/settings/brave-api-key');
}

// ── Match-Scoring-Settings ──────────────────────────────
export interface MatchSettings {
  country_mode: 'hard' | 'soft' | 'off';
  city_mode: 'fuzzy' | 'strict' | 'off';
  volume_tolerance_pct: number;
  size_tolerance_pct: number;
  units_tolerance_pct: number;
  walt_tolerance_pct: number;
  year_tolerance_years: number;
  bonus_location_quality: number;
  bonus_country: number;
  bonus_city_fuzzy: number;
  bonus_city_strict_extra: number;
  bonus_risk: number;
  bonus_tenants: number;
  bonus_esg: number;
  bonus_deal_structure: number;
  bonus_volume_centered: number;
  min_score: number;
  high_score: number;
  medium_score: number;
  weight_asset_default: number;
  weight_location_default: number;
  weight_financial_default: number;
  respect_profile_weights: boolean;
}

export async function getMatchSettings(): Promise<{ data: MatchSettings; defaults: MatchSettings }> {
  const { data } = await apiClient.get('/settings/match');
  return data;
}

export async function updateMatchSettings(updates: Partial<MatchSettings>): Promise<MatchSettings> {
  const { data } = await apiClient.put('/settings/match', updates);
  return data.data;
}

export async function resetMatchSettings(): Promise<MatchSettings> {
  const { data } = await apiClient.delete('/settings/match');
  return data.data;
}

export interface ExposeMessageOverride {
  company_id: string;
  to_email?:  string;
  subject?:   string;
  body_text?: string;
  cc_emails?: string[];
}

export interface SendAccount {
  id: string;
  label: string;
  from_email: string;
  from_name: string | null;
  is_default?: boolean;
  has_password?: boolean;
}

export async function getSendAccounts(): Promise<{
  resolved: SendAccount | null;
  matched_login: boolean;
  login_email: string | null;
  accounts: SendAccount[];
}> {
  const { data } = await apiClient.get('/email-accounts/for-send');
  return data.data;
}

export async function sendExpose(payload: {
  mandate_id: string;
  contact_ids: string[];
  messages?: ExposeMessageOverride[];
  email_account_id?: string | null;
}): Promise<{ data: { company: string; status: string; error?: string; to?: string; cc?: string[] }[]; sent: number; failed: number }> {
  const res = await apiClient.post('/send-expose', payload);
  // Backend-Antwort gruppiert: { data, sent, failed }. Wir leiten sent/failed auch ab,
  // falls sie nicht mehr im Payload enthalten sind.
  const data = res.data;
  const sent = data.sent ?? (data.data?.filter?.((r: { status: string }) => r.status === 'sent')?.length ?? 0);
  const failed = data.failed ?? (data.data?.filter?.((r: { status: string }) => r.status === 'error')?.length ?? 0);
  return { data: data.data, sent, failed };
}

export interface ExposePreview {
  company_id:                string;
  company_name:              string | null;
  postempfaenger_contact_id: string | null;
  postempfaenger_name:       string | null;
  to_email:                  string;
  cc_emails:                 string[];
  cc_contact_ids:            string[];
  subject:                   string;
  body_text:                 string;
  error?:                    string;
}

export async function getExposePreview(payload: {
  mandate_id: string;
  contact_ids: string[];
}): Promise<{ data: ExposePreview[] }> {
  const { data } = await apiClient.post('/send-expose/preview', payload);
  return data;
}
